import { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Crown,
  CreditCard,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Check,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useSubscription } from '@/hooks/useSubscription';
import { PRICING } from '@/lib/subscription';

const premiumFeatures = [
  'Importaciones PDF ilimitadas',
  'Bola de nieve y metas financieras',
  'Topes de gastos con alertas',
  'Panel anual completo',
  'Notificaciones por email',
  'Detalle completo del FOCO Score',
  'Chat con asesoría IA',
];

export default function Subscription() {
  const {
    isPremium,
    planLabel,
    expiresAt,
    portalLoading,
    openPortal,
    profile,
  } = useSubscription();

  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const isCancelled = profile?.subscription_status === 'cancelled';
  const isPastDue = profile?.subscription_status === 'past_due';

  const expiresLabel =
    expiresAt && format(new Date(expiresAt), "d 'de' MMMM yyyy", { locale: es });

  return (
    <AppLayout>
      <div className="max-w-2xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Link to="/settings" className="hover:text-foreground transition-colors">
            Configuración
          </Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground font-medium">Suscripción</span>
        </div>

        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Crown className="h-6 w-6" />
            Mi suscripción
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Administra tu plan, método de pago y cancelación
          </p>
        </div>

        {/* Estado del plan */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Plan actual</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">

            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-xl">{planLabel}</p>
                {expiresLabel && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {isCancelled
                      ? `Acceso hasta: ${expiresLabel}`
                      : `Próxima renovación: ${expiresLabel}`}
                  </p>
                )}
                {isPremium && (
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {PRICING.monthly.label} · Cancela cuando quieras
                  </p>
                )}
              </div>

              {isPremium && !isCancelled && !isPastDue && (
                <span className="inline-flex items-center gap-1.5 text-xs bg-yellow-400/10 text-yellow-500 px-3 py-1.5 rounded-full font-medium">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Activa
                </span>
              )}
              {isCancelled && (
                <span className="inline-flex items-center gap-1.5 text-xs bg-muted text-muted-foreground px-3 py-1.5 rounded-full font-medium">
                  <XCircle className="h-3.5 w-3.5" />
                  Cancelada
                </span>
              )}
              {isPastDue && (
                <span className="inline-flex items-center gap-1.5 text-xs bg-destructive/10 text-destructive px-3 py-1.5 rounded-full font-medium">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Pago pendiente
                </span>
              )}
            </div>

            {/* Alerta pago vencido */}
            {isPastDue && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <p>
                  No pudimos procesar tu último pago. Actualiza tu método de pago para mantener el acceso Premium.
                </p>
              </div>
            )}

            {/* Funciones incluidas */}
            {isPremium && (
              <>
                <Separator />
                <div>
                  <p className="text-sm font-medium mb-2">Incluido en tu plan</p>
                  <ul className="grid sm:grid-cols-2 gap-1.5">
                    {premiumFeatures.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Check className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </>
            )}

          </CardContent>
        </Card>

        {/* Acciones */}
        {isPremium && !isCancelled && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Administrar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">

              <Button
                variant="outline"
                className="w-full justify-start gap-3 h-11"
                onClick={() => openPortal()}
                disabled={portalLoading}
              >
                <CreditCard className="h-4 w-4" />
                <div className="text-left">
                  <p className="font-medium text-sm">
                    {portalLoading ? 'Abriendo portal…' : 'Gestionar facturación'}
                  </p>
                  <p className="text-xs text-muted-foreground font-normal">
                    Actualiza tu tarjeta, descarga facturas
                  </p>
                </div>
              </Button>

              <Separator />

              {/* Cancelación */}
              {!confirmingCancel ? (
                <button
                  className="text-sm text-muted-foreground hover:text-destructive transition-colors underline underline-offset-2 w-full text-left pt-1"
                  onClick={() => setConfirmingCancel(true)}
                >
                  Cancelar suscripción
                </button>
              ) : (
                <div className="space-y-3 p-4 rounded-lg border border-destructive/30 bg-destructive/5">
                  <div>
                    <p className="text-sm font-semibold text-destructive">¿Cancelar tu suscripción?</p>
                    <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
                      {expiresLabel
                        ? `Seguirás con acceso Premium hasta el ${expiresLabel}. No se realizarán más cobros después de esa fecha.`
                        : 'Seguirás con acceso Premium hasta el final del período actual.'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      className="flex-1"
                      onClick={() => { setConfirmingCancel(false); openPortal(); }}
                      disabled={portalLoading}
                    >
                      Sí, cancelar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setConfirmingCancel(false)}
                    >
                      Mantener Premium
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Pago vencido */}
        {isPastDue && (
          <Button
            className="w-full gap-2"
            onClick={() => openPortal()}
            disabled={portalLoading}
          >
            <CreditCard className="h-4 w-4" />
            Actualizar método de pago
          </Button>
        )}

        {/* Cancelada */}
        {isCancelled && (
          <Card>
            <CardContent className="pt-6 space-y-3">
              <p className="text-sm text-muted-foreground">
                Tu suscripción fue cancelada. Puedes reactivarla en cualquier momento.
              </p>
              <Button asChild className="w-full">
                <Link to="/upgrade">Reactivar suscripción</Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Plan gratuito */}
        {!isPremium && !isCancelled && (
          <Card>
            <CardContent className="pt-6 space-y-3">
              <p className="text-sm text-muted-foreground">
                Estás en el plan gratuito. Actualiza para desbloquear todas las funciones.
              </p>
              <Button asChild className="w-full">
                <Link to="/upgrade">Ver planes Premium</Link>
              </Button>
            </CardContent>
          </Card>
        )}

      </div>
    </AppLayout>
  );
}
